import React, { ClipboardEvent, useContext, useEffect, useState } from "react";
import FuturesTable from "./FuturesTable";
import { COPY_PASTE_SPLIT } from "../../../constants/app.constants";
import { useParams } from "react-router";
import { useLocation } from "react-router-dom";
import {
  createFuturesDefaultData,
  FuturesContext,
} from "../../../contexts/FuturesContext";
import { ForecastContext } from "../../../contexts/ForecastContext";
import { SeasonToMonthContext } from "../../../contexts/SeasonToMonthContext";
import { ShipmentHeaderContext } from "../../../contexts/ShipmentHeaderContext";
import DefaultFuturesTable from "./DefaultFuturesTable";
import {
  autoUpdateForecast,
  autoUpdateForecastInsideCalculations,
} from "../../../constants/utils";

const FuturesTableContainer = () => {
  const { tab } = useParams<{ tab: string }>();
  const { pathname } = useLocation();
  const is3PW = pathname.includes("3pw");
  const {
    horizontalHeaders,
    monthsInFirstTwoSeasons,
    verticalMonths,
    verticalSeasons,
  } = useContext(ShipmentHeaderContext);
  const {
    originalForecast: { futures_pg2n_totals, history_LR_totals },
  } = useContext(ForecastContext);
  const { cupData } = useContext(SeasonToMonthContext);
  const {
    futuresData,
    setFuturesData,
    futuresTotals,
    setFuturesTotals,
    selectedCell,
    setNextCell,
  } = useContext(FuturesContext);
  const lrTotals = cupData[cupData.length - 1];
  const [savedLRTotals, setSavedLRTotals] = useState(lrTotals);
  const ccdMonthTotal = history_LR_totals ? history_LR_totals[0] : 0;
  const [currentLocation, setCurrentLocation] = useState(pathname);
  useEffect(() => {
    if (pathname !== currentLocation) {
      setFuturesData(
        createFuturesDefaultData(verticalMonths, horizontalHeaders),
      );
      setCurrentLocation(pathname);
    }
  });

  useEffect(() => {
    if (pathname.includes("3pw")) {
      // base update on first change in cupData
      const index = lrTotals.findIndex(
        (val, index) => val !== savedLRTotals[index],
      );
      if (index === -1) return;
      const incomingData = futuresData.map((row) => [...row]);
      // only care about updating from column n forward; leave row at 0
      const totals = autoUpdateForecastInsideCalculations(
        incomingData,
        monthsInFirstTwoSeasons,
        futures_pg2n_totals,
        lrTotals,
        0,
        index,
        incomingData[0][index],
      );
      setFuturesTotals(totals);
      setSavedLRTotals(lrTotals);
      setFuturesData(incomingData);
    }
  }, [lrTotals]);

  const updateData = (row: number, column: number) => (value: string) => {
    let units = +value;

    const dataToModify = futuresData.map((row) => [...row]);
    if (is3PW) {
      dataToModify[row][column] = units;
      const totals =
        row > monthsInFirstTwoSeasons + 6 && column > monthsInFirstTwoSeasons
          ? autoUpdateForecastInsideCalculations(
              dataToModify,
              monthsInFirstTwoSeasons,
              futures_pg2n_totals,
              lrTotals,
              row,
              column,
              units,
            )
          : autoUpdateForecast(
              dataToModify,
              monthsInFirstTwoSeasons,
              futures_pg2n_totals,
              lrTotals,
              ccdMonthTotal,
              row,
              column,
              units,
            );
      setFuturesTotals(totals);
      setFuturesData(dataToModify);
    } else {
      const diff = units - dataToModify[row][column];
      dataToModify[row][column] = units;
      dataToModify[row][dataToModify[row].length - 1] += diff;
      setFuturesData(dataToModify);
      setFuturesTotals(dataToModify[0].slice(0, dataToModify[0].length - 1));
    }
  };

  const onPaste =
    (row: number, column: number) =>
    (e: ClipboardEvent<HTMLTableCellElement>) => {
      const incomingData = e.clipboardData.getData("text");
      const splitData = incomingData
        .split(COPY_PASTE_SPLIT)
        .filter((datum) => datum !== "")
        .map((datum) =>
          datum.split(/\t/).map((datum) => {
            if (!datum) {
              return 0;
            }
            return parseInt(datum.replaceAll(",", ""));
          }),
        );
      if (is3PW) {
        const rowsToUpdate = futuresData.slice(row, row + splitData.length);
        const updatedRows = rowsToUpdate.map((rowToUpdate, i) => {
          const updatedRow = rowToUpdate
            .slice(0, column)
            .concat(splitData[i])
            .concat(
              rowToUpdate.slice(
                column + splitData[i].length,
                rowToUpdate.length - 3,
              ),
            );
          const total = updatedRow
            .slice(monthsInFirstTwoSeasons)
            .reduce((acc, curr) => acc + curr, 0);
          return updatedRow
            .concat(total)
            .concat(rowToUpdate[rowToUpdate.length - 2])
            .concat(total - rowToUpdate[rowToUpdate.length - 2]);
        });
        const updatedData = futuresData
          .slice(0, row)
          .concat(updatedRows)
          .concat(futuresData.slice(row + splitData.length));

        // Slice off any data that can't be mapped to the existing data
        const differences = splitData
          .slice(0, verticalMonths.length - row)
          .map((datum, i) =>
            datum
              .slice(0, horizontalHeaders.MONTHS.length - column)
              .map((subDatum, j) => subDatum - rowsToUpdate[i][j + column]),
          )
          .reduce((acc, curr) => {
            if (acc.length === 0) {
              return curr;
            }

            return acc.map((val, i) => val + curr[i]);
          }, []);

        const updatedTotals = futuresTotals
          .slice(0, column)
          .concat(
            futuresTotals
              .slice(column, column + differences.length)
              .map((total, i) => total + differences[i]),
          )
          .concat(futuresTotals.slice(column + differences.length));
        // Check if this is a copy / paste over existing 3PW data or if it should be recalculated
        if (
          is3PW &&
          column + splitData[0].length <=
            horizontalHeaders.SEASONS[0].colSpan +
              horizontalHeaders.SEASONS[1].colSpan +
              1 &&
          row + splitData.length <= 12
        ) {
          const totals = autoUpdateForecast(
            updatedData,
            monthsInFirstTwoSeasons,
            futures_pg2n_totals,
            lrTotals,
            ccdMonthTotal,
          );
          setFuturesTotals(totals);
          setFuturesData(updatedData);
        } else {
          differences.forEach((diff) => {
            updatedData[row][updatedData[row].length - 1] += diff;
          });
          setFuturesTotals(updatedTotals);
          setFuturesData(updatedData);
        }
      } else {
        const updatedData = futuresData[0]
          .slice(0, column)
          .concat(splitData[0])
          .concat(futuresData[0].slice(column + splitData[0].length));
        updatedData[updatedData.length - 1] = updatedData
          .slice(0, updatedData.length - 1)
          .reduce((acc, curr) => acc + curr, 0);
        setFuturesData([updatedData]);
        setFuturesTotals(updatedData.slice(0, updatedData.length - 1));
      }
    };
  return (
    <>
      {is3PW ? (
        <FuturesTable
          data={futuresData}
          horizontalHeaders={horizontalHeaders}
          tab={tab.toUpperCase()}
          additionalHeaders={["Total", "Plan", "Var"]}
          verticalMonths={verticalMonths}
          verticalSeasons={verticalSeasons}
          monthsInFirstTwoSeasons={monthsInFirstTwoSeasons}
          onPaste={onPaste}
          updateData={updateData}
          totals={futuresTotals}
          selectedCell={selectedCell}
          setNextCell={setNextCell}
        />
      ) : (
        <DefaultFuturesTable
          data={futuresData}
          horizontalHeaders={horizontalHeaders}
          updateData={updateData}
          tab={tab.toUpperCase()}
          monthsInFirstTwoSeasons={monthsInFirstTwoSeasons}
          onPaste={onPaste}
          additionalHeaders={["Total"]}
          setNextCell={setNextCell}
          selectedCell={selectedCell}
        />
      )}
    </>
  );
};

export default FuturesTableContainer;
